import { CleverService, CleverAuthType } from './clever.service';
import { checkCleverIntegration } from './cleverAuth.service';
import { Toast } from '../components/Common/Toast';
import { AppDispatch } from '../store/store';
import { HTTPStatusEnum } from '../interfaces/log.interface';

export interface CleverTabAuthResult {
  success: boolean;
  error?: string;
}

export class CleverAuthTabService {
  /**
   * Open Clever authentication in new tab
   */
  static async openAuthTab(): Promise<CleverTabAuthResult> {
    return new Promise(async (resolve, reject) => {
      let checkClosed: NodeJS.Timeout | null = null;
      
      try {
        // Generate auth URL for new tab
        const authUrl = await CleverService.generateAuthUrl(CleverAuthType.TEACHER_LOGIN, true);

        // Open new tab
        const newTab = window.open(authUrl, '_blank');

        if (!newTab) {
          resolve({ success: false, error: 'New tab blocked by browser' });
          return;
        }

        // Listen for message from new tab
        const messageHandler = async (event: MessageEvent) => {
          console.log('Received message from new tab:', event.data);
          
          // Security check - verify origin
          if (event.origin !== window.location.origin) {
            console.log('Origin mismatch, ignoring message:', event.origin, 'expected:', window.location.origin);
            return;
          }

          if (event.data.type === 'CLEVER_AUTH_SUCCESS') {
            console.log('Received CLEVER_AUTH_SUCCESS message');
            window.removeEventListener('message', messageHandler);
            
            try {
              // Handle the OAuth callback (this includes token exchange and user data)
              const authResponse = await CleverService.exchangeCode(
                event.data.code,
                event.data.state
              );

              console.log('Auth response received:', authResponse);

              if (authResponse?.status === HTTPStatusEnum.OK) {
                // Clear any intervals
                if (checkClosed) {
                  clearInterval(checkClosed);
                  checkClosed = null;
                }

                // Don't close the tab here - let the callback page handle it
                // The callback page will close itself after showing success message
                console.log('Authentication successful, resolving promise');
                resolve({ success: true });
              } else {
                console.log('Invalid token response:', authResponse);
                resolve({ success: false, error: 'Invalid token response' });
              }
            } catch (error) {
              console.error('Error during authentication:', error);
              resolve({ success: false, error: error instanceof Error ? error.message : 'Authentication failed' });
            }
          } else if (event.data.type === 'CLEVER_AUTH_ERROR') {
            console.log('Received CLEVER_AUTH_ERROR message:', event.data.error);
            alert(event.data.error)
            window.removeEventListener('message', messageHandler);
            
            // Clear any intervals
            if (checkClosed) {
              clearInterval(checkClosed);
              checkClosed = null;
            }
            
            resolve({ success: false, error: event.data.error || 'Authentication failed' });
          }
        };

        window.addEventListener('message', messageHandler);

        // Handle new tab close
        // checkClosed = setInterval(() => {
        //   if (newTab.closed) {
        //     clearInterval(checkClosed!);
        //     checkClosed = null;
        //     window.removeEventListener('message', messageHandler);
        //     resolve({ success: false, error: 'Authentication cancelled' });
        //   }
        // }, 1000);

        // Timeout after 5 minutes
        setTimeout(() => {
          if (checkClosed) {
            clearInterval(checkClosed);
            checkClosed = null;
          }
          window.removeEventListener('message', messageHandler);
          if (!newTab.closed) {
            newTab.close();
          }
          resolve({ success: false, error: 'Authentication timeout' });
        }, 5 * 60 * 1000);

      } catch (error) {
        // Clear any intervals
        if (checkClosed) {
          clearInterval(checkClosed);
        }
        resolve({ success: false, error: error instanceof Error ? error.message : 'Authentication failed' });
      }
    });
  }

  /**
   * Authenticate with Clever using new tab and return result
   */
  static async authenticateWithTab(): Promise<boolean> {
    try {
      const result = await this.openAuthTab();

      if (result.success) {
        Toast('Successfully authenticated with Clever!', false);
        return true;
      } else {
        // Handle specific error cases
        if (result.error === 'New tab blocked by browser') {
          Toast('Please allow new tabs for this site to authenticate with Clever.', true);
        } else if (result.error === 'Authentication cancelled') {
          Toast('Authentication was cancelled.', true);
        } else if (result.error === 'Authentication timeout') {
          Toast('Authentication timed out. Please try again.', true);
        } else {
          Toast('Authentication failed: ' + result.error, true);
        }
        return false;
      }
    } catch (error) {
      console.error('Error during Clever authentication:', error);
      Toast('Authentication failed. Please try again.', true);
      return false;
    }
  }

  /**
   * Get token or authenticate if needed using new tab
   */
  static async getTokenOrAuthenticate(): Promise<boolean> {
    // Check if we have integration
    const hasIntegration = await checkCleverIntegration();
    if (hasIntegration) {
      return true;
    }

    // Try to authenticate via new tab
    return await this.authenticateWithTab();
  }

  static async cleverLogOut(): Promise<boolean> {
    try {
      // Clear Clever integration data
      const response = await CleverService.logoutFromCleverSync();

      // Optionally, you can also clear user data or redirect
      Toast('Successfully logged out from Clever', false);

      return response;
    } catch (error) {
      console.error('Error during Clever logout:', error);
      Toast('Failed to log out from Clever. Please try again.', true);
      return false;
    }
  }
} 